// client/src/pages/UploadPage.tsx
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import InfoModal from "../Components/InfoModal";
import { startUploadFlow, uploadToGCS } from "../services/api";
import apiClient from "../services/apiClient";
import type {FilesUploadedResponse} from "../types/upload";
import { useTranslation } from "react-i18next";

const UploadPage = () => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [text, setText] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    title: "",
    message: ""
  });
  const [showDescModal, setShowDescModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const allowedTypes = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  useEffect(() => {
    if (location.state?.jobDescription) {
      setText(location.state.jobDescription);
    }
  }, [location.state]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const newFiles = Array.from(e.target.files);

    const validFiles = newFiles.filter((file) => {
      const name = file.name.toLowerCase();

      return (
        file.type === "application/pdf" ||
        file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        name.endsWith(".pdf") ||
        name.endsWith(".docx")
      );
    });

    if (validFiles.length === 0) return;

    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name + f.size));
      return [
        ...prev,
        ...validFiles.filter(
          (f) => !existing.has(f.name + f.size)
        ),
      ];
    });

    e.target.value = "";
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      setInfoModal({
        isOpen: true,
        title: t("uploadPage.noFilesSelectedTitle"),
        message: t("uploadPage.noFilesSelectedMessage")
      });
      return;
    }
    if (!text || text.trim().length === 0) {
      setInfoModal({
        isOpen: true,
        title: t("uploadPage.missingJobDescriptionTitle"),
        message: t("uploadPage.missingJobDescriptionMessage")
      });
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const jobId = location.state?.jobId;
      if (!jobId) {
        setError(t("uploadPage.jobIdMissing"));
        return;
      }

      const { batchId, uploads } = await startUploadFlow(files, text, jobId);
      await uploadToGCS(files, uploads);
      console.log("✅ Files uploaded to GCS");

      const response = await apiClient.post<FilesUploadedResponse>("/upload/files-uploaded", {
        batch_id: batchId,
        job_id: jobId,
        notes: text,
        files: uploads.map((u: any) => ({
          file_name: u.file_name,
          file_path: u.file_path,
        })),
      });

      navigate("/result", {
        state: {
          application_ids: response.data.application_ids,
          jobDescription: text,
          jobId
        }
      });
    } catch (error) {
      setUploadProgress(0);
      const errorMessage = error instanceof Error ? error.message : t("uploadPage.uploadFailed");
      setError(errorMessage);
      console.error("Uploads failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen w-full">
      <div className="w-1/2 flex flex-col justify-center items-center p-10 bg-gray-50 shadow-inner">
        <h1 className="text-4xl font-extrabold mb-6 text-gray-900">{t("uploadPage.title")}</h1>

        <div className="mb-6 w-full max-w-md">
          <input
            id="file-upload"
            type="file"
            accept=".pdf,.docx"
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />

          <input
            id="folder-upload"
            type="file"
            multiple
            onChange={handleFileUpload}
            className="hidden"
            {...{ webkitdirectory: "true", directory: "true" }}
          />
          <div className="flex gap-4">
            <label
              htmlFor="file-upload"
              className="cursor-pointer flex-1 text-center px-4 py-3 bg-primary text-white font-semibold rounded-xl shadow-sm hover:bg-blue-800 transition-all"
            >
              {t("uploadPage.chooseFiles")}
            </label>

            <label
              htmlFor="folder-upload"
              className="cursor-pointer flex-1 text-center px-4 py-3 bg-primary text-white font-semibold rounded-xl shadow-sm hover:bg-blue-800 transition-all"
            >
              {t("uploadPage.chooseFolder")}
            </label>
          </div>
          <div className="mt-3 space-y-3">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
              <p>
                <span className="font-medium">{t("uploadPage.uploadLimitLabel")}</span> {t("uploadPage.uploadLimitText")}
              </p>
              <p>
                <span className="font-medium">{t("uploadPage.supportedFormatsLabel")}</span> {t("uploadPage.supportedFormatsText")}
              </p>
            </div>

            <p
              className={`text-sm text-center ${files.length > 20 ? "text-red-600 font-semibold" : "text-gray-500"
                }`}
            >
              {t(files.length === 1 ? "uploadPage.fileSelected" : "uploadPage.filesSelected", { count: files.length })}
            </p>
          </div>
        </div>

        <div className="mb-6 w-full max-w-md">
          {location.state?.jobTitle && (
            <p className="text-sm text-gray-700 mb-2">{t("uploadPage.jobDescriptionFor")} <strong>{location.state.jobTitle}</strong></p>
          )}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center gap-2 flex-wrap">
            <p className="text-gray-800">
              {text.length > 25 ? text.slice(0, 25).trimEnd() + "\u2026" : text}
            </p>
            {text.length > 25 && (
              <button
                onClick={() => setShowDescModal(true)}
                className="text-primary text-sm font-semibold hover:underline whitespace-nowrap"
              >
                {t("uploadPage.readMore")}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="w-full max-w-md p-3 mb-4 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Submit */}
        {/* <button
          onClick={handleSubmit}
          disabled={isLoading}
          className={`w-full max-w-md py-3 rounded-xl font-semibold shadow-sm transition-all ${isLoading
            ? "bg-gray-300 cursor-not-allowed text-gray-500"
            : "bg-primary text-white hover:bg-blue-800 hover:text-secondary"
            }`}
        >
          {isLoading ? "⏳ Processing..." : "Submit"}
        </button> */}

        {/* Progress */}
        {/* {uploadProgress > 0 && (
          <div className="w-full max-w-md mt-4 bg-gray-200 rounded-full h-3">
            <div
              className="bg-primary h-3 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        )} */}
      </div>

      {/* Right: Preview Section */}
      <div className="w-1/2 flex flex-col p-10 bg-white border-l border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{t("uploadPage.profileName")}</h2>
        {files.length > 0 ? (
          <div className="space-y-3 w-full max-w-2xl flex-1 overflow-y-auto pr-2">
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-white border border-gray-200 shadow-sm p-3 rounded-lg hover:shadow-md transition-all"
              >
                <span className="text-sm font-medium text-gray-700 truncate">
                  {index + 1}. {file.name}
                </span>
                <button
                  onClick={() => handleRemoveFile(index)}
                  className="text-gray-400 hover:text-red-500 font-bold transition-colors"
                >
                  ✖
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 italic">{t("uploadPage.noFilesUploaded")}</p>
        )}
        <div className="sticky bottom-0 bg-white pt-4 pb-2 mt-auto">
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className={`w-full py-3 rounded-xl font-semibold shadow-sm transition-all ${isLoading
              ? "bg-gray-300 cursor-not-allowed text-gray-500"
              : "bg-primary text-white hover:bg-blue-800"
              }`}
          >
            {isLoading ? t("uploadPage.processing") : t("uploadPage.submit")}
          </button>

          {uploadProgress > 0 && (
            <div className="w-full mt-3 bg-gray-200 rounded-full h-3">
              <div
                className="bg-primary h-3 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}
        </div>
      </div>

      <InfoModal
        isOpen={infoModal.isOpen}
        title={infoModal.title}
        message={infoModal.message}
        onClose={() => setInfoModal({ ...infoModal, isOpen: false })}
      />

      {/* Job Description Modal */}
      {showDescModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDescModal(false)}
        >
          <div
            className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 text-gray-900"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-800">
                {t("uploadPage.jobDescription")}{location.state?.jobTitle ? ` - ${location.state.jobTitle}` : ""}
              </h3>
              <button
                onClick={() => setShowDescModal(false)}
                className="text-gray-400 hover:text-gray-700 transition-colors text-xl leading-none"
                aria-label={t("uploadPage.close")}
              >
                &times;
              </button>
            </div>
            {/* Body */}
            <div className="max-h-80 overflow-y-auto pr-1">
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {text}
              </p>
            </div>
            {/* Footer */}
            <div className="flex justify-end mt-5">
              <button
                onClick={() => setShowDescModal(false)}
                className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-blue-800 transition-colors"
              >
                {t("uploadPage.close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadPage;
